import React from 'react';
import { Redirect, useRouteMatch } from 'react-router-dom';
import { useQuery } from 'react-apollo';
import { useSelector } from 'react-redux';

import { AlbumFolderWithPrepaidItemsInfo } from '../../../graphql/queries/album-folder';
import Spinner from '../../global/Spinner';
import {
  MeWithPurchasedItems,
  MeWithSelectedIdentities,
} from '../../../graphql/queries/account';
import { PURCHASED_PRINT_PACKAGE_STATUS } from '../../../constants/print-packages';
import { PURCHASED_PHOTO_PRICING_STATUS } from '../../../constants/photo-pricings';
import { PURCHASED_CUSTOM_PRINT_STATUS } from '../../../constants/custom-prints';

const AlbumFolderAllowsPrePaidItems = ({ forceRedirect } = {}) => {
  return WrappedComponent => props => {
    const match = useRouteMatch();
    const { albumFolderId } = match?.params ?? {};
    const authenticated = useSelector(({ auth }) => auth.authenticated);

    const { data: { albumFolder } = {}, loading } = useQuery(
      AlbumFolderWithPrepaidItemsInfo,
      {
        variables: {
          id: albumFolderId,
        },
        skip: !albumFolderId,
      },
    );
    const { data: { me: meWithIdentities } = {}, loading: loadingAccount } =
      useQuery(MeWithSelectedIdentities, {
        variables: {
          albumFolderIds: [albumFolderId],
        },
        fetchPolicy: 'network-only',
        skip: !authenticated || !albumFolderId,
      });

    const {
      data: { me: meWithPurchasedItems } = {},
      loading: loadingPurchases,
    } = useQuery(MeWithPurchasedItems, {
      variables: {
        is_prepaid: true,
        album_folder_id: albumFolderId,
        print_packages_status: PURCHASED_PRINT_PACKAGE_STATUS.PENDING,
        photo_pricings_status: PURCHASED_PHOTO_PRICING_STATUS.PENDING,
        custom_prints_statuses: [PURCHASED_CUSTOM_PRINT_STATUS.CUSTOMER_INPUT],
      },
      fetchPolicy: 'network-only',
      skip: !authenticated || !albumFolderId,
    });

    if (loading || loadingAccount || loadingPurchases) {
      return (
        <div className="wrapper-main min-h-100vh justify-content-center align-items-center">
          <Spinner />
        </div>
      );
    }

    if (!loading && !albumFolder) {
      return <Redirect to="/not-found" />;
    }

    if (!authenticated) {
      return <WrappedComponent {...props} />;
    }

    // For Allowing prepaid items, we have the following rules:
    // 1. Album Folder must allow Pre-paid (prepaid_print_packages_enabled or prepaid_photo_pricing_enabled)
    if (
      !albumFolder.prepaid_print_packages_enabled &&
      !albumFolder.prepaid_photo_pricings_enabled &&
      !albumFolder.prepaid_custom_prints_enabled
    ) {
      return <WrappedComponent {...props} />;
    }

    const canPrePurchasePackages =
      albumFolder.prepaid_print_packages_enabled &&
      albumFolder.album_folder_print_packages?.total > 0;
    const canPrePurchasePrints =
      albumFolder.prepaid_photo_pricings_enabled &&
      !!albumFolder.photo_pricing?.id;
    const canPrePurchaseCustomPrints =
      albumFolder.prepaid_custom_prints_enabled &&
      albumFolder.album_custom_prints?.total > 0;

    // 2. Album Folder must be Picture Day & have the respective pre-purchase options available
    if (
      !albumFolder.picture_day_enabled ||
      (!canPrePurchasePackages &&
        !canPrePurchasePrints &&
        !canPrePurchaseCustomPrints)
    ) {
      return <WrappedComponent {...props} />;
    }

    // 3. Album Folder must not require purchases to join
    if (albumFolder.join_requires_purchase) {
      return <WrappedComponent {...props} />;
    }

    // if the 3 rules above are covered, we can act based off the props submitted.
    // One of said states that if we have joined identities & no purchases we force
    // a redirect.
    const subbedIdentities =
      meWithIdentities?.selected_identities?.records ?? [];
    const prepaidPackages =
      meWithPurchasedItems?.purchased_print_packages?.records ?? [];
    const prepaidPhotoPricings =
      meWithPurchasedItems?.purchased_photo_pricings?.records ?? [];
    const prepaidCustomPrints =
      meWithPurchasedItems?.purchased_custom_prints?.records ?? [];

    if (
      forceRedirect &&
      subbedIdentities.length > 0 &&
      prepaidPackages.length === 0 &&
      prepaidPhotoPricings.length === 0 &&
      prepaidCustomPrints.length === 0
    ) {
      return (
        <Redirect to={`/discover/album-folder/${albumFolderId}/pre-purchase`} />
      );
    }

    return (
      <WrappedComponent
        {...props}
        allowsPrepaidItems
        prepaidPrintPackagePromoCode={
          albumFolder.active_promo_codes?.records?.[0]
        }
      />
    );
  };
};

export default AlbumFolderAllowsPrePaidItems;
